import type { CalculationMode } from '../constants.js';
import {
  decimalsForPrecision,
  isMeasurable,
  nearestMeasurableWeights,
  percentToMgPerGram,
  thcMgFromGrams,
  unitsToGrams,
} from '../units.js';

/**
 * Single-strain “solve for weight given target THC” calculator.
 *
 * Formula (percentage points, not fractions):
 *   exactGrams = targetThcMg ÷ (thcPercent × 10)
 *
 * Scale rounding then picks a measurable weight using Closest / At Least /
 * Do Not Exceed. `target_range` is not applicable for single-strain solves.
 */

/** Modes supported by the single-strain weight solver. */
export type SingleStrainRoundingMode =
  | 'closest'
  | 'at_least'
  | 'do_not_exceed';

export type SingleStrainInput = {
  /** Display name for the selected strain. */
  strainName: string;
  /** Canonical THC percentage points (0–100). */
  thcPercent: number | null | undefined;
  /** Target THC in milligrams. */
  targetThcMg: number;
  /** Scale step in grams (e.g. 0.01). */
  scalePrecisionGrams: number;
  /** Rounding / preference mode. */
  mode: SingleStrainRoundingMode | CalculationMode;
};

export type SingleStrainResult = {
  ok: true;
  strainName: string;
  thcPercent: number;
  targetThcMg: number;
  scalePrecisionGrams: number;
  mode: SingleStrainRoundingMode;
  /** Exact algebraic weight before scale snapping. */
  exactWeightGrams: number;
  /** Scale-compatible recommended weight. */
  recommendedWeightGrams: number;
  /** Estimated THC at the recommended weight. */
  estimatedThcMg: number;
  /** estimated − target (positive = over). */
  differenceMg: number;
  /** True when recommended weight equals the exact weight (on-scale). */
  exactIsMeasurable: boolean;
};

export type SingleStrainFailure = {
  ok: false;
  code:
    | 'missing_thc'
    | 'zero_thc'
    | 'invalid_thc'
    | 'missing_target'
    | 'invalid_target'
    | 'non_finite'
    | 'unsupported_mode';
  message: string;
};

export type SingleStrainOutcome = SingleStrainResult | SingleStrainFailure;

/**
 * Forward solve: THC at a chosen joint weight
 * (`THC mg = grams × thcPercent × 10`).
 */
export type SingleStrainFromWeightInput = {
  strainName: string;
  thcPercent: number | null | undefined;
  weightGrams: number;
  scalePrecisionGrams: number;
};

export type SingleStrainFromWeightResult = {
  ok: true;
  strainName: string;
  thcPercent: number;
  /** Entered / requested joint weight. */
  weightGrams: number;
  /** Weight used for the estimate (scale-snapped when needed). */
  measurableWeightGrams: number;
  estimatedThcMg: number;
  scalePrecisionGrams: number;
  exactIsMeasurable: boolean;
};

export type SingleStrainFromWeightFailure = {
  ok: false;
  code:
    | 'missing_thc'
    | 'zero_thc'
    | 'invalid_thc'
    | 'missing_weight'
    | 'invalid_weight'
    | 'non_finite';
  message: string;
};

export type SingleStrainFromWeightOutcome =
  | SingleStrainFromWeightResult
  | SingleStrainFromWeightFailure;

const MG_EPS = 1e-9;

function isSingleMode(
  mode: SingleStrainRoundingMode | CalculationMode,
): mode is SingleStrainRoundingMode {
  return mode === 'closest' || mode === 'at_least' || mode === 'do_not_exceed';
}

/**
 * Estimate THC milligrams for a chosen joint weight of one strain.
 * Uses the canonical `thcMgFromGrams` helper; snaps display weight to the
 * scale when the entered value is not measurable.
 */
export function estimateSingleStrainFromWeight(
  input: SingleStrainFromWeightInput,
): SingleStrainFromWeightOutcome {
  const { strainName, scalePrecisionGrams } = input;

  if (
    input.thcPercent === null ||
    input.thcPercent === undefined ||
    Number.isNaN(input.thcPercent)
  ) {
    return {
      ok: false,
      code: 'missing_thc',
      message:
        'This strain has no usable THC percentage, so THC cannot be estimated.',
    };
  }
  if (!Number.isFinite(input.thcPercent)) {
    return {
      ok: false,
      code: 'invalid_thc',
      message: 'This strain’s THC percentage is invalid and cannot be used.',
    };
  }
  if (input.thcPercent < 0 || input.thcPercent > 100) {
    return {
      ok: false,
      code: 'invalid_thc',
      message: 'This strain’s THC percentage is out of range and cannot be used.',
    };
  }
  if (input.thcPercent === 0) {
    return {
      ok: false,
      code: 'zero_thc',
      message:
        'This strain reports 0% THC, so THC cannot be estimated for a weight.',
    };
  }

  if (
    input.weightGrams === null ||
    input.weightGrams === undefined ||
    Number.isNaN(input.weightGrams as number)
  ) {
    return {
      ok: false,
      code: 'missing_weight',
      message: 'Enter a joint weight in grams.',
    };
  }
  if (!Number.isFinite(input.weightGrams)) {
    return {
      ok: false,
      code: 'invalid_weight',
      message: 'Joint weight must be a finite number in grams.',
    };
  }
  if (input.weightGrams <= 0) {
    return {
      ok: false,
      code: 'invalid_weight',
      message: 'Joint weight must be greater than zero.',
    };
  }

  if (!Number.isFinite(scalePrecisionGrams) || scalePrecisionGrams <= 0) {
    return {
      ok: false,
      code: 'non_finite',
      message: 'Scale precision must be a positive number.',
    };
  }

  const decimals = decimalsForPrecision(scalePrecisionGrams);
  const exactIsMeasurable = isMeasurable(
    input.weightGrams,
    scalePrecisionGrams,
  );
  const estimatedThcMg = thcMgFromGrams(input.weightGrams, input.thcPercent);
  if (!Number.isFinite(estimatedThcMg)) {
    return {
      ok: false,
      code: 'non_finite',
      message: 'THC could not be estimated from these inputs.',
    };
  }

  return {
    ok: true,
    strainName,
    thcPercent: input.thcPercent,
    weightGrams: input.weightGrams,
    measurableWeightGrams: roundGrams(input.weightGrams, decimals),
    estimatedThcMg,
    scalePrecisionGrams,
    exactIsMeasurable,
  };
}

/**
 * Compute the exact required weight and a scale-friendly recommendation.
 */
export function calculateSingleStrain(
  input: SingleStrainInput,
): SingleStrainOutcome {
  const { strainName, scalePrecisionGrams } = input;

  if (!isSingleMode(input.mode)) {
    return {
      ok: false,
      code: 'unsupported_mode',
      message:
        'Target-range mode applies to blends only. Choose Closest, At Least, or Do Not Exceed.',
    };
  }
  const mode = input.mode;

  if (
    input.thcPercent === null ||
    input.thcPercent === undefined ||
    Number.isNaN(input.thcPercent)
  ) {
    return {
      ok: false,
      code: 'missing_thc',
      message:
        'This strain has no usable THC percentage, so a weight cannot be calculated.',
    };
  }
  if (!Number.isFinite(input.thcPercent)) {
    return {
      ok: false,
      code: 'invalid_thc',
      message: 'This strain’s THC percentage is invalid and cannot be used.',
    };
  }
  if (input.thcPercent < 0 || input.thcPercent > 100) {
    return {
      ok: false,
      code: 'invalid_thc',
      message: 'This strain’s THC percentage is out of range and cannot be used.',
    };
  }
  if (input.thcPercent === 0) {
    return {
      ok: false,
      code: 'zero_thc',
      message:
        'This strain reports 0% THC, so a weight cannot be calculated for a THC target.',
    };
  }

  if (
    input.targetThcMg === null ||
    input.targetThcMg === undefined ||
    Number.isNaN(input.targetThcMg as number)
  ) {
    return {
      ok: false,
      code: 'missing_target',
      message: 'Enter a target THC amount in milligrams.',
    };
  }
  if (!Number.isFinite(input.targetThcMg)) {
    return {
      ok: false,
      code: 'invalid_target',
      message: 'Target THC must be a finite number in milligrams.',
    };
  }
  if (input.targetThcMg <= 0) {
    return {
      ok: false,
      code: 'invalid_target',
      message: 'Target THC must be greater than zero.',
    };
  }

  if (
    !Number.isFinite(scalePrecisionGrams) ||
    scalePrecisionGrams <= 0
  ) {
    return {
      ok: false,
      code: 'non_finite',
      message: 'Scale precision must be a positive number.',
    };
  }

  const mgPerGram = percentToMgPerGram(input.thcPercent);
  const exactWeightGrams = input.targetThcMg / mgPerGram;
  if (!Number.isFinite(exactWeightGrams) || exactWeightGrams < 0) {
    return {
      ok: false,
      code: 'non_finite',
      message: 'The required weight could not be calculated from these inputs.',
    };
  }

  const recommendedWeightGrams = recommendMeasurableWeight({
    exactWeightGrams,
    thcPercent: input.thcPercent,
    targetThcMg: input.targetThcMg,
    scalePrecisionGrams,
    mode,
  });

  if (!Number.isFinite(recommendedWeightGrams) || recommendedWeightGrams < 0) {
    return {
      ok: false,
      code: 'non_finite',
      message: 'The recommended scale weight could not be determined.',
    };
  }

  const estimatedThcMg = thcMgFromGrams(
    recommendedWeightGrams,
    input.thcPercent,
  );
  const decimals = decimalsForPrecision(scalePrecisionGrams);

  return {
    ok: true,
    strainName,
    thcPercent: input.thcPercent,
    targetThcMg: input.targetThcMg,
    scalePrecisionGrams,
    mode,
    exactWeightGrams,
    recommendedWeightGrams: roundGrams(recommendedWeightGrams, decimals),
    estimatedThcMg,
    differenceMg: estimatedThcMg - input.targetThcMg,
    exactIsMeasurable: isMeasurable(exactWeightGrams, scalePrecisionGrams),
  };
}

function roundGrams(grams: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(grams * factor) / factor;
}

/**
 * Pick a measurable weight for the chosen rounding mode.
 *
 * Tie-break (Closest when |diff| equal): prefer the candidate whose estimated
 * THC is at or below target; if still tied, prefer the lower weight.
 */
function recommendMeasurableWeight(args: {
  exactWeightGrams: number;
  thcPercent: number;
  targetThcMg: number;
  scalePrecisionGrams: number;
  mode: SingleStrainRoundingMode;
}): number {
  const {
    exactWeightGrams,
    thcPercent,
    targetThcMg,
    scalePrecisionGrams,
    mode,
  } = args;

  if (isMeasurable(exactWeightGrams, scalePrecisionGrams)) {
    return exactWeightGrams;
  }

  if (mode === 'closest') {
    return closestMeasurable({
      exactWeightGrams,
      thcPercent,
      targetThcMg,
      scalePrecisionGrams,
    });
  }

  if (mode === 'at_least') {
    // Smallest measurable weight with estimated THC ≥ target.
    const { higher } = nearestMeasurableWeights(
      exactWeightGrams,
      scalePrecisionGrams,
    );
    // If exact sits between steps, `higher` is the first ≥ exact weight; THC
    // rises with weight, so that is also the first ≥ target (when exact isn't
    // measurable). Guard zero: if target needs at least one step.
    let candidate = higher;
    if (thcMgFromGrams(candidate, thcPercent) + MG_EPS < targetThcMg) {
      // Walk up if floating edge cases leave us short (shouldn't normally).
      let units = Math.round(candidate / scalePrecisionGrams);
      while (
        thcMgFromGrams(unitsToGrams(units, scalePrecisionGrams), thcPercent) +
          MG_EPS <
        targetThcMg
      ) {
        units += 1;
        if (units > 1_000_000) break;
      }
      candidate = unitsToGrams(units, scalePrecisionGrams);
    }
    return candidate;
  }

  // do_not_exceed: largest measurable weight with estimated THC ≤ target.
  const { lower } = nearestMeasurableWeights(
    exactWeightGrams,
    scalePrecisionGrams,
  );
  let candidate = lower;
  if (thcMgFromGrams(candidate, thcPercent) - MG_EPS > targetThcMg) {
    let units = Math.round(candidate / scalePrecisionGrams);
    while (
      units > 0 &&
      thcMgFromGrams(unitsToGrams(units, scalePrecisionGrams), thcPercent) -
        MG_EPS >
        targetThcMg
    ) {
      units -= 1;
    }
    candidate = unitsToGrams(units, scalePrecisionGrams);
  }
  return Math.max(0, candidate);
}

function closestMeasurable(args: {
  exactWeightGrams: number;
  thcPercent: number;
  targetThcMg: number;
  scalePrecisionGrams: number;
}): number {
  const { exactWeightGrams, thcPercent, targetThcMg, scalePrecisionGrams } =
    args;
  const { lower, higher } = nearestMeasurableWeights(
    exactWeightGrams,
    scalePrecisionGrams,
  );
  const lowerThc = thcMgFromGrams(lower, thcPercent);
  const higherThc = thcMgFromGrams(higher, thcPercent);
  const lowerDiff = Math.abs(lowerThc - targetThcMg);
  const higherDiff = Math.abs(higherThc - targetThcMg);

  if (Math.abs(lowerDiff - higherDiff) <= MG_EPS) {
    // Prefer at/below target; then lower weight.
    const lowerOk = lowerThc <= targetThcMg + MG_EPS;
    const higherOk = higherThc <= targetThcMg + MG_EPS;
    if (lowerOk && !higherOk) return lower;
    if (higherOk && !lowerOk) return higher;
    return lower <= higher ? lower : higher;
  }
  return lowerDiff < higherDiff ? lower : higher;
}
